import React, { useState, useEffect, useCallback } from 'react'
import { formatDate } from '../../utils/format-date'
import { useCashSession } from '../../hooks/useCashSession'
import { toast } from 'react-toastify'
import { getMovements, withdrawCash } from '../../services/CashService'
import type { CashSummary, Movement } from '../../types/@types/cash'
import { SessionPanel } from './SessionPanel'
import { MovementList } from './MovementList'
import { MetricCard } from './MetricCard'
import { CloseModal } from './CloseModal'
import { WithdrawModal } from './WithdrawModal'
import {
  DollarSign,
  BarChart2,
  Calculator,
  ArrowDownCircle,
  MinusCircle,
} from 'lucide-react'
import { formatCOP } from '../../utils/money'

export default function CashSessionManager() {
  const {
    session,
    loading,
    error,
    openSession,
    opening,
    closeSession,
    closing,
  } = useCashSession()
  const [amount, setAmount] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)
  const [summary, setSummary] = useState<CashSummary | null>(null)
  const [movements, setMovements] = useState<Movement[]>([])
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [showClose, setShowClose] = useState(false)
  const [showWithdraw, setShowWithdraw] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)

  const fetchData = useCallback(async () => {
    if (!session?.id) return
    try {
      setLoadingSummary(true)
      const mov = await getMovements(session.id).catch(() => [] as Movement[])
      setMovements(mov)
      const initialAmount = Number(session.initialAmount) || 0
      const totalSales = mov.reduce(
        (acc, m) =>
          m && m.type === 'sale' ? acc + (Number(m.amount) || 0) : acc,
        0
      )
      const cashSales = mov.reduce(
        (acc, m) => {
          if (!m || m.type !== 'sale') return acc;
          const method = (m.method || '').toLowerCase()
          return (method === 'cash' || method === 'efectivo' || !method)
            ? acc + (Number(m.amount) || 0)
            : acc
        },
        0
      )
      const withdrawals = mov.reduce(
        (acc, m) =>
          m && m.type === 'withdraw' ? acc + (Number(m.amount) || 0) : acc,
        0
      )
      const expenses = mov.reduce(
        (acc, m) =>
          m && m.type === 'expense' ? acc + (Number(m.amount) || 0) : acc,
        0
      )
      setSummary({
        initialAmount,
        totalSales,
        cashSales,
        withdrawals,
        expenses,
        expectedInDrawer: initialAmount + cashSales - withdrawals - expenses,
      })
    } catch (err) {
      toast.error('No se pudo actualizar los totales')
    } finally {
      setLoadingSummary(false)
      setRefreshing(false)
    }
  }, [session])

  useEffect(() => {
    if (!session?.id) {
      setSummary(null)
      setMovements([])
      return
    }
    fetchData()
    const interval = setInterval(fetchData, 30000)
    const handler = () => fetchData()
    window.addEventListener('cash:updated', handler)
    return () => {
      clearInterval(interval)
      window.removeEventListener('cash:updated', handler)
    }
  }, [session, fetchData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (amount <= 0) {
      setFormError('Monto inicial inválido')
      return
    }
    setFormError(null)
    await openSession(amount)
  }

  const handleCloseSession = async (finalAmount: number, obs: string) => {
    const success = await closeSession(finalAmount, obs)
    if (success) {
      toast.success('Caja cerrada')
      setShowClose(false)
    } else {
      toast.error('No se pudo cerrar la caja')
    }
  }

  const handleWithdraw = async (amount: number, reason: string) => {
    if (!session?.id) return
    if (amount <= 0) {
      toast.error('Monto inválido')
      return
    }
    setWithdrawing(true)
    try {
      await withdrawCash({ session: session.id, amount, reason })
      await fetchData()
      toast.success('Retiro registrado')
      setShowWithdraw(false)
    } catch (err: any) {
      if (err.response?.data?.message) {
        toast.error(err.response.data.message)
      } else {
        toast.error('No se pudo registrar el retiro')
      }
    } finally {
      setWithdrawing(false)
    }
  }

  const handleRefresh = () => {
    if (!session?.id) return
    setRefreshing(true)
    fetchData()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        Cargando...
      </div>
    )
  }

  if (!session || !session.id) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow p-6 space-y-4 w-full max-w-sm"
        >
          <h2 className="text-xl font-semibold text-center">Abrir Caja</h2>
          <p className="text-sm text-center text-gray-600">
            Caja cerrada / abre una sesión para ver totales
          </p>
          {error && <p className="text-red-600 text-sm text-center">{error}</p>}
          <div>
            <label className="block text-sm font-medium">Monto inicial</label>
            <input
              type="number"
              min={1}
              value={amount}
              onChange={e => setAmount(Number(e.target.value))}
              className="mt-1 w-full border rounded px-3 py-2"
            />
            {formError && (
              <p className="text-red-600 text-sm mt-1">{formError}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={opening}
            className="w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded py-2 disabled:opacity-50"
          >
            {opening ? 'Abriendo...' : 'Abrir sesión'}
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <MetricCard
          icon={<DollarSign />}
          label="Apertura"
          value={formatCOP(summary?.initialAmount ?? 0)}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-blue-500 to-blue-700"
        />
        <MetricCard
          icon={<BarChart2 />}
          label="Ventas Total"
          value={formatCOP(summary?.totalSales ?? 0)}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-green-500 to-green-700"
          subtitle="incluye todos los medios"
        />
        <MetricCard
          icon={<DollarSign />}
          label="Ventas Efectivo"
          value={formatCOP(summary?.cashSales ?? 0)}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-yellow-400 to-yellow-600"
          subtitle="solo efectivo"
        />
        <MetricCard
          icon={<ArrowDownCircle />}
          label="Retiros"
          value={formatCOP(summary?.withdrawals ?? 0)}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-orange-500 to-orange-700"
        />
        <MetricCard
          icon={<MinusCircle />}
          label="Gastos"
          value={formatCOP(summary?.expenses ?? 0)}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-red-500 to-red-700"
        />
        <MetricCard
          icon={<Calculator />}
          label="Esperado en Caja"
          value={formatCOP(summary?.expectedInDrawer ?? 0)}
          loading={loadingSummary}
          gradient="bg-gradient-to-r from-purple-500 to-purple-700"
          subtitle="Apertura + Efectivo − Retiros − Gastos"
          alert={(summary?.expectedInDrawer ?? 0) < 0}
        />
      </div>
      <SessionPanel
        sessionId={session.id}
        openedAt={formatDate(session.openedAt)}
        onWithdraw={() => setShowWithdraw(true)}
        onClose={() => setShowClose(true)}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
      <WithdrawModal
        visible={showWithdraw}
        onClose={() => setShowWithdraw(false)}
        onConfirm={handleWithdraw}
        loading={withdrawing}
      />
      <CloseModal
        visible={showClose}
        onClose={() => setShowClose(false)}
        onConfirm={handleCloseSession}
        loading={closing}
      />
      <MovementList movements={movements} />
    </div>
  )
}
